import { api } from "../api/Middleware";
import { CUSTOMER_PAYMENTS_URL } from "../api/Urls";

// =============================
// CUSTOMER SUBSCRIPTION SERVICES
// =============================

// URLs específicas para assinaturas
const SUBSCRIPTIONS_URL = CUSTOMER_PAYMENTS_URL;
const ACTIVE_SUBSCRIPTIONS_URL = CUSTOMER_PAYMENTS_URL + "active_subscriptions/";
const PAYMENT_SUMMARY_URL = CUSTOMER_PAYMENTS_URL + "payment_summary/";
const CREATE_SUBSCRIPTION_URL = CUSTOMER_PAYMENTS_URL + "create_subscription/";
const CANCEL_SUBSCRIPTION_URL = CUSTOMER_PAYMENTS_URL + "cancel_subscription/";
const SUBSCRIPTION_DETAILS_URL = CUSTOMER_PAYMENTS_URL + "subscription_details/";
const PLAN_SUBSCRIPTION_INFO_URL = CUSTOMER_PAYMENTS_URL + "plan_subscription_info/";
const PLAN_INVOICES_URL = CUSTOMER_PAYMENTS_URL + "plan_invoices/";

// Listar todas as assinaturas (com filtros)
export const listSubscriptions = async (params = {}) => {
  return await api.get(SUBSCRIPTIONS_URL, { params });
};

// Listar apenas assinaturas ativas
export const listActiveSubscriptions = async (params = {}) => {
  return await api.get(ACTIVE_SUBSCRIPTIONS_URL, { params });
};

// Obter resumo de pagamentos
export const getPaymentSummary = async (params = {}) => {
  return await api.get(PAYMENT_SUMMARY_URL, { params });
};

// Criar nova assinatura
export const createSubscription = async (data) => {
  return await api.post(CREATE_SUBSCRIPTION_URL, data);
};

// Cancelar assinatura
export const cancelSubscription = async (subscriptionId, reason = "Cancelamento solicitado pelo usuário", internal = false) => {
  const data = {
    subscription_id: subscriptionId,
    reason: reason,
    internal: internal
  };
  
  return await api.post(CANCEL_SUBSCRIPTION_URL, data);
};

// Obter detalhes completos de uma assinatura específica
export const getSubscriptionDetails = async (subscriptionId) => {
  return await api.get(`${SUBSCRIPTION_DETAILS_URL}?subscription_id=${subscriptionId}`);
};

// Obter logs de uma assinatura
export const getSubscriptionLogs = async (subscriptionId) => {
  return await api.get(`${SUBSCRIPTIONS_URL}${subscriptionId}/logs/`);
};

// Obter informações da assinatura de plano do usuário
export const getPlanSubscriptionInfo = async () => {
  return await api.get(PLAN_SUBSCRIPTION_INFO_URL);
};

// Listar faturas da assinatura de plano
export const getPlanInvoices = async (params = {}) => {
  return await api.get(PLAN_INVOICES_URL, { params });
};

// =============================
// HELPER FUNCTIONS
// =============================

// Obter label amigável do tipo de contrato
export const getContractTypeLabel = (subscription) => {
  if (subscription.contract_type === 'agent') return 'Publicar Agente';
  if (subscription.contract_type === 'subaccount' || subscription.subscription_type === 'subaccount') return 'Subconta';
  if (subscription.subscription_type === 'feature_upgrade') return 'Upgrade de Feature';
  if (subscription.subscription_type === 'marketplace_agent') return 'Agente Marketplace';
  if (subscription.subscription_type === 'addon') return 'Serviço Adicional';
  return subscription.subscription_type_display || 'Outro';
};

// Obter variante do badge para tipo de contrato
export const getContractTypeVariant = (subscription) => {
  if (subscription.contract_type === 'agent') return 'info';
  if (subscription.subscription_type === 'subaccount') return 'primary';
  if (subscription.subscription_type === 'feature_upgrade') return 'warning';
  if (subscription.subscription_type === 'marketplace_agent') return 'success';
  if (subscription.subscription_type === 'addon') return 'secondary';
  return 'secondary';
};

// Formatar dados da assinatura para exibição
export const formatSubscriptionData = (subscription) => {
  if (!subscription) return {};

  return {
    ...subscription,
    formattedValue: formatCurrency(subscription.value),
    formattedNextDueDate: formatDate(subscription.next_due_date),
    formattedCreatedAt: formatDate(subscription.created_at),
    statusText: getStatusText(subscription.status_display, subscription.status),
    statusVariant: getStatusVariant(subscription.status_display, subscription.status),
    recurrencyText: getRecurrencyText(subscription.recurrency),
    recurrencyVariant: getRecurrencyVariant(subscription.recurrency),
    contractTypeLabel: getContractTypeLabel(subscription),
    contractTypeVariant: getContractTypeVariant(subscription),
    paymentProgress: subscription.max_payments
      ? Math.round(
          ((subscription.current_payment_count || 0) /
            subscription.max_payments) *
            100
        )
      : null,
  };
};

// Formatar valor para exibição
export const formatCurrency = (value) => {
  if (!value) return "R$ 0,00";
  
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(parseFloat(value));
};

// Formatar data para exibição
export const formatDate = (dateString) => {
  if (!dateString) return "—";
  try {
    let date = dateString.split("T")[0];
    date = date.split("-").reverse().join("/");
    return date
  } catch (error){
    return "Data inválida";
  }
};

// Obter texto do status
export const getStatusText = (status, status_id=false) => {
  const statusMap = {
    1: "Aguardando",
    2: "Confirmado", 
    3: "Vencido",
    4: "Recusado",
    5: "Expirado",
    6: "Cancelado",
    "ACTIVE": "Ativo",
    "INACTIVE": "Inativo",
    "PENDING": "Pendente",
    "CANCELED": "Cancelado",
    "IN_USE": "Em Uso",
    "AVAILABLE": "Disponível"
  };

  if (status_id && status_id === 6) {
    return "Cancelado"
  }
  
  return statusMap[status] || `Status ${status}`;
};

// Obter variante do badge para status
export const getStatusVariant = (status, status_id=false) => {
  const variantMap = {
    1: "warning", // Aguardando
    2: "success", // Confirmado
    3: "danger", // Vencido
    4: "danger", // Recusado
    5: "secondary", // Expirado
    6: "dark", // Cancelado
    ACTIVE: "success",
    INACTIVE: "secondary",
    PENDING: "warning",
    CANCELLED: "dark",
    IN_USE: "primary",
    AVAILABLE: "success",
  };

  if (status_id && status_id === 6) {
    return "danger";
  }

  return variantMap[status] || "secondary";
};

// Obter texto da recorrência
export const getRecurrencyText = (recurrency) => {
  const recurrencyMap = {
    "MONTHLY": "Mensal",
    "QUARTERLY": "Trimestral", 
    "SEMIANNUALLY": "Semestral",
    "YEARLY": "Anual",
  };
  
  return recurrencyMap[recurrency] || recurrency;
};

// Obter variante do badge para recorrência
export const getRecurrencyVariant = (recurrency) => {
  const variantMap = {
    "MONTHLY": "primary",
    "QUARTERLY": "info",
    "SEMIANNUALLY": "warning",
    "YEARLY": "success",
  };
  
  return variantMap[recurrency] || "secondary";
};

// Construir parâmetros de busca
export const buildSearchParams = (filters) => {
  const params = {};
  
  Object.keys(filters).forEach(key => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value;
    }
  });
  
  return params;
};

// Obter filtros disponíveis
export const getAvailableFilters = () => {
  return {
    status: [
      { value: "", label: "Todos os Status" },
      { value: "1", label: "Aguardando" },
      { value: "2", label: "Confirmado" },
      { value: "3", label: "Vencido" },
      { value: "4", label: "Recusado" },
      { value: "5", label: "Expirado" },
      { value: "6", label: "Cancelado" },
    ],
    recurrency: [
      { value: "", label: "Todas as Recorrências" },
      { value: "MONTHLY", label: "Mensal" },
      { value: "QUARTERLY", label: "Trimestral" },
      { value: "SEMIANNUALLY", label: "Semestral" },
      { value: "YEARLY", label: "Anual" },
    ],
    is_active_subscription: [
      { value: "", label: "Todos" },
      { value: "true", label: "Assinaturas Ativas" },
      { value: "false", label: "Assinaturas Inativas" },
    ],
  };
};

// Validar permissões (baseado no padrão dos outros serviços)
export const canPerformAction = (action, userContext, subscription = null) => {
  const { isOrganization, user } = userContext;
  
  // Verificar se é organização
  if (!isOrganization()) {
    return false;
  }
  
  // Verificar nível de usuário (superuser_level <= 2 tem mais permissões)
  switch (action) {
    case "view":
      return user?.superuser_level <= 3;
      
    case "create":
    case "edit":
      return user?.superuser_level <= 2;
      
    case "cancel":
    case "delete":
      return user?.superuser_level <= 1;
      
    default:
      return false;
  }
};

// Processar resposta de erro da API
export const processSubscriptionError = (error) => {
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;
    
    // Erro específico de agente ainda implementado
    if (data?.agent_deployment_id && data?.agent_name) {
      return {
        message: data.error || "Não é possível cancelar esta assinatura. Primeiro cancele a implementação do agente associado antes de prosseguir com o cancelamento da assinatura.",
        status,
        details: data,
        type: 'agent_deployed'
      };
    }
    
    // Erro de assinatura já cancelada
    if (data?.errors && Array.isArray(data.errors)) {
      const canceledError = data.errors.find(err => err.code === "SUBSCRIPTION_ALREADY_CANCELED");
      if (canceledError) {
        return {
          message: canceledError.description || "A assinatura já foi cancelada",
          status,
          details: data,
          type: 'already_canceled'
        };
      }
    }
    
    if (status === 400 && data) {
      // Outros erros de validação
      if (typeof data === 'object') {
        if (data.error) {
          return {
            message: data.error,
            status,
            details: data,
          };
        }
        const messages = Object.values(data).flat();
        return {
          message: messages.join(', '),
          status,
          details: data,
        };
      }
    }
    
    if (status === 404) {
      return {
        message: data?.error || "Assinatura não encontrada ou já cancelada",
        status,
      };
    }
    
    if (status === 403) {
      return {
        message: "Sem permissão para esta operação",
        status,
      };
    }
    
    // Outros erros HTTP
    return {
      message: data?.message || data?.error || "Erro no servidor",
      status,
    };
  }
  
  // Erro de rede
  return {
    message: "Erro de conexão. Verifique sua internet.",
    status: null,
  };
};

// Obter texto do status de pagamento (faturas Asaas)
export const getInvoiceStatusText = (status) => {
  const statusMap = {
    "PENDING": "Pendente",
    "CONFIRMED": "Pago",
    "RECEIVED": "Recebido",
    "RECEIVED_IN_CASH": "Recebido",
    "OVERDUE": "Vencido",
    "REFUNDED": "Estornado",
    "REFUND_REQUESTED": "Estorno Solicitado",
    "REFUND_IN_PROGRESS": "Estorno em Andamento",
    "CHARGEBACK_REQUESTED": "Contestação",
    "CHARGEBACK_DISPUTE": "Em Disputa",
    "DUNNING_REQUESTED": "Em Recuperação",
    "DUNNING_RECEIVED": "Recuperado",
    "AWAITING_RISK_ANALYSIS": "Em Análise",
  };
  return statusMap[status] || status || "Desconhecido";
};

// Obter variante do badge para status de fatura
export const getInvoiceStatusVariant = (status) => {
  const variantMap = {
    "PENDING": "warning",
    "CONFIRMED": "success",
    "RECEIVED": "success",
    "RECEIVED_IN_CASH": "success",
    "OVERDUE": "danger",
    "REFUNDED": "info",
    "REFUND_REQUESTED": "info",
    "REFUND_IN_PROGRESS": "info",
    "CHARGEBACK_REQUESTED": "danger",
    "CHARGEBACK_DISPUTE": "danger",
    "DUNNING_REQUESTED": "warning",
    "DUNNING_RECEIVED": "success",
    "AWAITING_RISK_ANALYSIS": "secondary",
  };
  return variantMap[status] || "secondary";
};

export default {
  listSubscriptions,
  listActiveSubscriptions,
  getPaymentSummary,
  createSubscription,
  cancelSubscription,
  getSubscriptionDetails,
  getSubscriptionLogs,
  getPlanSubscriptionInfo,
  getPlanInvoices,
  formatSubscriptionData,
  formatCurrency,
  formatDate,
  getStatusText,
  getStatusVariant,
  getRecurrencyText,
  getRecurrencyVariant,
  getContractTypeLabel,
  getContractTypeVariant,
  getInvoiceStatusText,
  getInvoiceStatusVariant,
  buildSearchParams,
  getAvailableFilters,
  canPerformAction,
  processSubscriptionError,
};